import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import {
  CartAddEvent,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  ThemeEvents,
} from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {object} CartDrawerRefs
 * @property {HTMLDialogElement} drawer - The cart drawer dialog element.
 * @property {HTMLFormElement[]} dialogForms - Forms with method="dialog" inside the drawer.
 * @property {HTMLElement} cartItems - Container for cart items.
 * @property {HTMLElement} cartItemCount - Hidden element containing cart item count.
 * @property {HTMLElement[]} quantitySelectors - The quantity selector components.
 *
 * @extends {Component<CartDrawerRefs>}
 */
class CartDrawer extends Component {
  /** @type {boolean} */
  isClosing = false;
  /** @type {(() => void) | null} */
  originalClose = null;
  /** @type {string | undefined} */
  sectionId;
  /** @type {HTMLElement | null} */
  backdrop = null;
  /** @type {boolean} */
  #mouseDownOnBackdrop = false;
  /** @type {AbortController | null} */
  #cartUpdateController = null;
  /** @type {(event: Event) => void} */
  _closeHandler = this.#handleClose.bind(this);
  /** @type {(event: Event) => void} */
  _cancelHandler = this.#handleClose.bind(this);
  /** @type {(event: Event) => void} */
  _sectionSelectHandler = this.#handleSectionSelect.bind(this);
  /** @type {(event: Event) => void} */
  _sectionDeselectHandler = this.#handleSectionDeselect.bind(this);
  /** @type {(event: Event) => void} */
  _cartUpdateHandler = this.#handleCartUpdate.bind(this);
  /** @type {(event: Event) => void} */
  _quantitySelectorUpdateHandler = this.#onQuantityChange.bind(this);
  /** @type {(event: Event) => void} */
  _moveToWishlistHandler = this.#onMoveToWishlist.bind(this);

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.drawer || !(this.refs.drawer instanceof HTMLDialogElement)) return;

    // Récupérer l'ID de la section depuis l'attribut data
    this.sectionId = this.dataset.sectionId;

    // Récupérer le backdrop personnalisé
    const drawerId = this.refs.drawer.id;
    this.backdrop = document.querySelector(`[data-drawer-backdrop="${drawerId}"]`);

    this.#setupEventListeners();
    this.#setupCartUpdateListener();
    this.#setupCartAddListener();
    this.#setupQuantitySelectorListener();
    this.#setupMoveToWishlistListener();
    this.#pruneOrphanEngravingFees();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.drawer) {
      this.refs.drawer.removeEventListener('close', this._closeHandler);
      this.refs.drawer.removeEventListener('cancel', this._cancelHandler);
    }

    // Nettoyer les écouteurs d'événements de l'éditeur de thème
    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.removeEventListener('shopify:section:select', this._sectionSelectHandler);
      document.removeEventListener('shopify:section:deselect', this._sectionDeselectHandler);
    }

    // Nettoyer les écouteurs d'événements cart
    document.removeEventListener(ThemeEvents.cartUpdate, this._cartUpdateHandler);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this._quantitySelectorUpdateHandler);
    document.removeEventListener('click', this._moveToWishlistHandler);
  }

  #setupEventListeners() {
    if (!this.refs.drawer) return;

    // Sauvegarder la méthode close originale en premier
    this.originalClose = this.refs.drawer.close.bind(this.refs.drawer);

    // Écouter les événements de l'éditeur de thème Shopify
    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this._sectionSelectHandler);
      document.addEventListener('shopify:section:deselect', this._sectionDeselectHandler);
    }

    // Écouter les événements natifs du dialog
    this.refs.drawer.addEventListener('close', this._closeHandler);
    this.refs.drawer.addEventListener('cancel', this._cancelHandler);

    // Fermer le dialog au clic sur le backdrop
    // On vérifie que le mousedown ET le click sont sur le backdrop pour éviter
    // de fermer lors d'un drag qui commence dans le contenu et se termine sur le backdrop
    this.refs.drawer.addEventListener('mousedown', (event) => {
      this.#mouseDownOnBackdrop = event.target === this.refs.drawer;
    });

    this.refs.drawer.addEventListener('click', (event) => {
      // Si le clic est directement sur le dialog (backdrop) ET que le mousedown était aussi sur le backdrop
      if (!this.refs.drawer || !this.originalClose) return;
      if (event.target === this.refs.drawer && this.#mouseDownOnBackdrop) {
        if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
          this.#animateClose(() => {
            if (this.originalClose) {
              this.originalClose();
            }
          });
        }
      }
      // Réinitialiser le flag
      this.#mouseDownOnBackdrop = false;
    });

    // Fermer le dialog au clic sur le backdrop personnalisé
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        if (!this.refs.drawer || !this.originalClose) return;
        if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
          this.#animateClose(() => {
            if (this.originalClose) {
              this.originalClose();
            }
          });
        }
      });
    }

    // Intercepter les soumissions de formulaire qui ferment le dialog
    if (Array.isArray(this.refs.dialogForms)) {
      for (const form of this.refs.dialogForms) {
        form.addEventListener('submit', (event) => {
          if (this.refs.drawer && this.refs.drawer.hasAttribute('open') && this.originalClose) {
            event.preventDefault();
            this.#animateClose(() => {
              if (this.originalClose) {
                this.originalClose();
              }
            });
          }
        });
      }
    }

    // S'assurer que le drawer commence caché (100% + 1.6rem pour le décalage)
    this.refs.drawer.style.transform = 'translateX(calc(100% + 1.6rem))';

    // Observer les changements d'attribut open pour déclencher les animations
    const observer = new MutationObserver(() => {
      if (!this.refs.drawer) return;

      if (this.refs.drawer.hasAttribute('open')) {
        // Ouverture
        this.isClosing = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#handleOpen();
          });
        });
      } else if (!this.isClosing) {
        // Fermeture (seulement si on n'est pas déjà en train de fermer)
        this.#handleClose();
      }
    });

    observer.observe(this.refs.drawer, {
      attributes: true,
      attributeFilter: ['open']
    });

    // Intercepter showModal pour déclencher l'animation d'ouverture
    const originalShowModal = this.refs.drawer.showModal.bind(this.refs.drawer);
    const originalShow = this.refs.drawer.show.bind(this.refs.drawer);

    this.refs.drawer.showModal = () => {
      if (!this.refs.drawer) return;
      // S'assurer que le drawer commence caché avant d'ouvrir (100% + 1.6rem pour le décalage)
      this.refs.drawer.style.transform = 'translateX(calc(100% + 1.6rem))';
      originalShowModal();
      // L'animation sera déclenchée par le MutationObserver
    };

    this.refs.drawer.show = () => {
      if (!this.refs.drawer) return;
      // S'assurer que le drawer commence caché avant d'ouvrir (100% + 1.6rem pour le décalage)
      this.refs.drawer.style.transform = 'translateX(calc(100% + 1.6rem))';
      originalShow();
      // L'animation sera déclenchée par le MutationObserver
    };

    // Intercepter close pour animer avant de fermer
    this.refs.drawer.close = () => {
      if (!this.refs.drawer || !this.originalClose) return;
      // Si le drawer est ouvert, animer avant de fermer
      if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
        this.#animateClose(() => {
          if (this.originalClose) {
            this.originalClose();
          }
        });
      } else {
        this.originalClose();
      }
    };
  }

  #handleOpen() {
    if (!this.refs.drawer) return;

    // Afficher le backdrop personnalisé
    if (this.backdrop) {
      this.backdrop.classList.add('active');
    }

    document.querySelector('header-component')?.classList.add('header--drawer-open');

    // S'assurer que le drawer est bien positionné à droite
    this.refs.drawer.style.right = '1.6rem';
    this.refs.drawer.style.left = 'auto';

    // S'assurer que le drawer commence caché (hors écran à droite, 100% + 1.6rem pour le décalage)
    this.refs.drawer.style.transform = 'translateX(calc(100% + 1.6rem))';

    // Forcer un reflow pour que le navigateur applique le style initial
    this.refs.drawer.offsetHeight;

    // Utiliser double requestAnimationFrame pour garantir que le navigateur
    // a appliqué le style initial avant de déclencher l'animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.refs.drawer) return;
        // Changer directement le transform pour déclencher la transition
        this.refs.drawer.style.transform = 'translateX(-1.6rem)';
        this.refs.drawer.classList.add('cart-drawer--opening');
        this.refs.drawer.classList.add('drawer-shell--opening');
      });
    });
  }

  /**
   * @param {() => void} callback
   */
  #animateClose(callback) {
    if (!this.refs.drawer) return;

    // Marquer qu'on est en train de fermer pour éviter les appels multiples
    this.isClosing = true;

    // Retirer la classe d'ouverture
    this.refs.drawer.classList.remove('cart-drawer--opening');
    this.refs.drawer.classList.remove('drawer-shell--opening');

    // Animer vers la droite (hors écran, 100% + 1.6rem pour le décalage)
    this.refs.drawer.style.transform = 'translateX(calc(100% + 1.6rem))';

    // Attendre la fin de l'animation avant de fermer
    setTimeout(() => {
      callback();
      this.isClosing = false;
    }, 300); // Durée de la transition CSS (0.3s)
  }

  #handleClose() {
    // Cette méthode est appelée après la fermeture pour nettoyer
    if (!this.refs.drawer) return;
    this.refs.drawer.classList.remove('cart-drawer--opening');
    this.refs.drawer.classList.remove('drawer-shell--opening');
    this.refs.drawer.style.transform = 'translateX(calc(100% + 1.6rem))';

    // Masquer le backdrop personnalisé
    if (this.backdrop) {
      this.backdrop.classList.remove('active');
    }

    // Retirer la classe header--drawer-open du header
    const header = document.querySelector('header-component');
    if (header) {
      header.classList.remove('header--drawer-open');
    }
  }

  /**
   * @param {Event} event
   */
  #handleSectionSelect(event) {
    if (!this.refs.drawer) return;

    // Vérifier si l'événement concerne cette section
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId === this.sectionId) {
      // Ouvrir le drawer dans l'éditeur
      if (!this.refs.drawer.hasAttribute('open')) {
        this.refs.drawer.showModal();
      }
    }
  }

  /**
   * @param {Event} event
   */
  #handleSectionDeselect(event) {
    if (!this.refs.drawer || !this.originalClose) return;

    // Vérifier si l'événement concerne cette section
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId === this.sectionId) {
      // Fermer le drawer dans l'éditeur
      if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
        this.#animateClose(() => {
          if (this.originalClose) {
            this.originalClose();
          }
        });
      }
    }
  }

  #setupCartUpdateListener() {
    document.addEventListener(ThemeEvents.cartUpdate, this._cartUpdateHandler);
  }

  #setupCartAddListener() {
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #setupQuantitySelectorListener() {
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this._quantitySelectorUpdateHandler);
  }

  #setupMoveToWishlistListener() {
    document.addEventListener('click', this._moveToWishlistHandler);
  }

  /**
   * Handles the move to wishlist action from the cart page.
   * @param {Event} event - The click event.
   */
  #onMoveToWishlist(event) {
    const target = /** @type {Element | null} */ (event.target);

    if (!target?.closest('.cart__item-wishlist-btn')) return;

    const container = target.closest('.cart__item-wishlist');
    if (!container) return;

    const line = parseInt(/** @type {HTMLElement} */(container).dataset.cartLine ?? '', 10);
    if (!line) return;

    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const wishlistBtn = container.querySelector('wishlist-button');
    if (!wishlistBtn) return;

    const openDialogsBefore = new Set(document.querySelectorAll('dialog[open]'));

    wishlistBtn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));

    setTimeout(() => {
      for (const dialog of document.querySelectorAll('dialog[open]')) {
        if (!openDialogsBefore.has(dialog)) {
          dialog.close();
        }
      }
    }, 100);
  }

  /**
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    // Only react to actual CartAddEvent, not CartUpdateEvent (they share the same event name)
    if (!(event instanceof CartAddEvent)) return;

    // Don't auto-open the drawer on the /cart page
    if (document.querySelector('[data-section-type="cart"]')) return;

    // Ouvrir le drawer automatiquement si l'attribut auto-open est présent
    // et si le drawer n'est pas déjà ouvert
    if (this.hasAttribute('auto-open') && this.refs.drawer && !this.refs.drawer.hasAttribute('open')) {
      this.refs.drawer.showModal();
    }
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Handles the line item removal.
   * Called via on:click="/onLineItemRemove/{lineIndex}"
   * @param {number|Event} lineOrEvent - The line item index (from on:click) or the event object.
   * @param {Event} [event] - The event object (if line was passed as first param).
   */
  onLineItemRemove(lineOrEvent, event) {
    // The component.js system passes data as first arg, event as second
    // So if we have 2 args, first is the line number, second is the event
    // If we have 1 arg and it's a number, it's the line number
    const line = event ? lineOrEvent : (typeof lineOrEvent === 'number' ? lineOrEvent : null);

    if (!line || typeof line !== 'number') {
      console.error('onLineItemRemove: line parameter is missing or invalid', { line, lineOrEvent, event });
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Builds a /cart/update.js payload applying a new quantity to a piece and
   * keeping its engraving fee line in sync. Both are linked by a shared token.
   *
   * A single fee line covers every piece sharing the same token: pieces of
   * different variants engraved in one confirmation reuse the token, and their
   * fee line items (identical properties, same variant) are merged by Shopify
   * onto one line. Its quantity must therefore equal the total quantity of
   * those pieces, recomputed from the DOM (using the new value for the changed
   * line) rather than mirrored from a single piece, otherwise removing one of
   * several linked pieces would clear the shared fee entirely.
   *
   * @param {number} line - The cart line being updated.
   * @param {number} quantity - The new quantity.
   * @returns {Record<string, number> | null} The updates, or null when the line carries no engraving fee.
   */
  #linkedEngravingUpdates(line, quantity) {
    const productLine = document.querySelector(`[data-cart-line="${line}"][data-engraving-token]`);
    const token = productLine?.getAttribute('data-engraving-token');
    const productKey = productLine?.getAttribute('data-line-key');

    if (!token || !productKey) return null;

    // Scope to the piece's own section so the drawer and the /cart page,
    // which render the same cart side by side, are never counted twice.
    const root = productLine.closest('.shopify-section') || document;

    const feeKey = root.querySelector(`[data-engraving-fee="${token}"]`)?.getAttribute('data-line-key');

    if (!feeKey) return null;

    let feeQuantity = 0;
    root.querySelectorAll(`[data-engraving-token="${token}"]`).forEach((piece) => {
      if (piece.getAttribute('data-cart-line') === String(line)) {
        feeQuantity += quantity;
      } else {
        const quantityInput = /** @type {HTMLInputElement | null} */ (piece.querySelector('input[name="updates[]"]'));
        feeQuantity += (quantityInput && parseInt(quantityInput.value, 10)) || 0;
      }
    });

    return { [productKey]: quantity, [feeKey]: feeQuantity };
  }

  /**
   * Finds the sections a cart mutation should re-render: this drawer's
   * section, every other cart-drawer instance on the page, and the /cart
   * page section when present.
   *
   * @returns {{sectionsToUpdate: Set<string>, cartPageSectionId: string | null | undefined}}
   */
  #collectSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-drawer');
    const sectionsToUpdate = new Set(this.sectionId ? [this.sectionId] : []);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const cartPageSection = document.querySelector('[data-section-type="cart"]');
    const cartPageSectionId = cartPageSection?.closest('[data-section-id]')?.getAttribute('data-section-id')
      || cartPageSection?.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    if (cartPageSectionId) {
      sectionsToUpdate.add(cartPageSectionId);
    }

    return { sectionsToUpdate, cartPageSectionId };
  }

  /**
   * Safety net for engraving fee lines left without the piece they belong
   * to (e.g. removed by a third-party app or the Shop app, outside this
   * theme's own removal flow, which always clears both lines together).
   * Runs once per page load; silently removes any orphan fee line found,
   * with no UI feedback since this repairs a pre-existing cart state rather
   * than reacting to a user action.
   */
  #pruneOrphanEngravingFees() {
    const orphanKeys = Array.from(document.querySelectorAll('[data-engraving-fee]'))
      .filter((fee) => !document.querySelector(`[data-engraving-token="${fee.getAttribute('data-engraving-fee')}"]`))
      .map((fee) => fee.getAttribute('data-line-key'))
      .filter((key) => key);

    if (orphanKeys.length === 0) return;

    const updates = Object.fromEntries(orphanKeys.map((key) => [key, 0]));
    const { sectionsToUpdate, cartPageSectionId } = this.#collectSectionsToUpdate();

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((parsedResponseText) => {
        if (!parsedResponseText.sections) return;

        if (this.sectionId && parsedResponseText.sections[this.sectionId]) {
          this.#updateQuantitySelectors(parsedResponseText);

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: parsedResponseText.item_count,
              source: 'cart-drawer',
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]).then(() => {
            this.#updateCartQuantitySelectorButtonStates();
          });
        }

        if (cartPageSectionId && parsedResponseText.sections[cartPageSectionId]) {
          morphSection(cartPageSectionId, parsedResponseText.sections[cartPageSectionId]);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (!this.sectionId) return;

    // Abort any previous pending request
    if (this.#cartUpdateController) {
      this.#cartUpdateController.abort();
    }

    // Create a new AbortController for this request
    this.#cartUpdateController = new AbortController();
    const signal = this.#cartUpdateController.signal;

    this.#disableCartItems();
    this.#shimmerCartItemImage(config.line);

    const { line, quantity } = config;

    const { sectionsToUpdate, cartPageSectionId } = this.#collectSectionsToUpdate();

    // The engraving fee lives on its own line item: it has to follow the
    // quantity of the piece it belongs to. Both lines are then updated at
    // once, keyed by line item key so shifting line indexes can't be hit.
    const linkedUpdates = this.#linkedEngravingUpdates(line, quantity);

    const body = JSON.stringify({
      ...(linkedUpdates ? { updates: linkedUpdates } : { line: line, quantity: quantity }),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const updateUrl = linkedUpdates ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;

    fetch(`${updateUrl}`, { ...fetchConfig('json', { body }), signal })
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        // /cart/change.js reports errors in `errors`, /cart/update.js in `description`.
        const errorMessage = parsedResponseText.errors || parsedResponseText.description || null;
        const hasError = !!errorMessage;
        const hasSections = this.sectionId && parsedResponseText.sections && parsedResponseText.sections[this.sectionId];

        // If we have sections, use them to update the cart first
        if (hasSections && this.sectionId) {
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            'text/html'
          );

          // Grab the new cart item count from a hidden element in the new HTML
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          // Update data-cart-quantity for all matching variants
          this.#updateQuantitySelectors(parsedResponseText);

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-drawer',
              sections: parsedResponseText.sections,
            })
          );

          const wasOpen = this.refs.drawer?.hasAttribute('open');

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]).then(() => {
            this.#updateCartQuantitySelectorButtonStates();
            this.#resetShimmerCartItemImage(line);

            // Only reopen the drawer if it was already open before morph
            if (wasOpen && this.refs.drawer && !this.refs.drawer.hasAttribute('open')) {
              this.refs.drawer.show();
            }

            if (this.refs.drawer?.hasAttribute('open')) {
              this.refs.drawer.parentElement?.querySelector('.drawer-shell__backdrop')?.classList.add('active');
            }

            // Also morph the cart page section if available
            if (cartPageSectionId && parsedResponseText.sections[cartPageSectionId]) {
              morphSection(cartPageSectionId, parsedResponseText.sections[cartPageSectionId]);
            }

            // Display error AFTER morph so it's not overwritten
            if (hasError && errorMessage) {
              // Use requestAnimationFrame to ensure morph is complete
              requestAnimationFrame(() => {
                this.#handleCartError(line, { errors: errorMessage });
              });
            }
          });
        } else if (hasError && this.sectionId) {
          const wasOpen = this.refs.drawer?.hasAttribute('open');
          // If there's an error but no sections, the server may have still updated the cart
          // (e.g., partial quantity added). Force a refresh to get the updated state.
          sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
            this.#updateCartQuantitySelectorButtonStates();
            this.#resetShimmerCartItemImage(line);

            // Only reopen the drawer if it was already open before refresh
            if (wasOpen && this.refs.drawer && !this.refs.drawer.hasAttribute('open')) {
              this.refs.drawer.show();
            }

            if (this.refs.drawer?.hasAttribute('open')) {
              this.refs.drawer.parentElement?.querySelector('.drawer-shell__backdrop')?.classList.add('active');
            }

            // Also refresh the cart page section if available
            if (cartPageSectionId) {
              sectionRenderer.renderSection(cartPageSectionId, { cache: false });
            }

            // Display error AFTER section refresh
            if (errorMessage) {
              this.#handleCartError(line, { errors: errorMessage });
            }
          });
        } else if (hasError) {
          // If there's an error but no sections and no sectionId, just display the error
          this.#handleCartError(line, { errors: errorMessage });
        }
      })
      .catch((error) => {
        // Ignore AbortError - this is expected when a newer request cancels this one
        if (error.name === 'AbortError') {
          return;
        }
        console.error(error);
      })
      .finally(() => {
        // Only re-enable if this request wasn't aborted
        if (!signal.aborted) {
          this.#enableCartItems();
          this.#resetShimmerCartItemImage(config.line);
        }
      });
  }

  /**
   * Applies shimmer effect to cart item image during loading
   * @param {number} line - The cart line index
   */
  #shimmerCartItemImage(line) {
    const imageWrapper = this.refs[`cartItemImage-${line}`];
    if (imageWrapper instanceof HTMLElement) {
      imageWrapper.setAttribute('shimmer', '');
    }
  }

  /**
   * Removes shimmer effect from cart item image
   * @param {number} line - The cart line index
   */
  #resetShimmerCartItemImage(line) {
    const imageWrapper = this.refs[`cartItemImage-${line}`];
    if (imageWrapper instanceof HTMLElement) {
      imageWrapper.removeAttribute('shimmer');
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) return;

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
    }
    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;


    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    if (!this.refs.cartItems) return;

    const cartQuantitySelectors = this.refs.cartItems.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * @param {Event} event
   */
  async #handleCartUpdate(event) {
    if (!this.sectionId) return;
    if (event.target === this) return;

    // Vérifier que c'est un événement CartAddEvent ou CartUpdateEvent
    if (!(event instanceof CartAddEvent) && !(event instanceof CartUpdateEvent)) return;

    // Ignorer les events déjà émis par ce drawer pour éviter une boucle infinie
    if (event.detail?.data?.source === 'cart-drawer') return;

    const sections = event.detail?.data?.sections;
    if (!sections || !this.sectionId) return;

    const sectionId = this.sectionId;

    // Sauvegarder l'état d'ouverture du drawer avant le morph
    const wasOpen = this.refs.drawer?.hasAttribute('open');

    try {
      // Extract actual cart total from sections HTML before morphing
      const newSectionHTML = sections[sectionId]
        ? new DOMParser().parseFromString(sections[sectionId], 'text/html')
        : null;
      const newCartItemCount = newSectionHTML
        ? parseInt(newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10)
        : 0;

      if (sections[sectionId]) {
        // Morph the section with the new HTML
        await morphSection(sectionId, sections[sectionId]);
        // Update button states for all cart quantity selectors after morph
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        // Fallback: re-render the entire section
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }

      // Dispatch a CartUpdateEvent with the actual total item count for the header badge
      document.dispatchEvent(
        new CartUpdateEvent({}, sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-drawer',
          sections,
        })
      );

      // Si le drawer était ouvert, s'assurer qu'il reste ouvert après le morph
      if (wasOpen && this.refs.drawer && !this.refs.drawer.hasAttribute('open')) {
        this.refs.drawer.show();
      }

      if (this.refs.drawer?.hasAttribute('open')) {
        this.refs.drawer.parentElement?.querySelector('.drawer-shell__backdrop')?.classList.add('active');
      }
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

